/**
 * Coerce a value to a number if possible. Returns NaN if not numeric.
 */
function toNumber(val) {
    if (typeof val === "number")
        return val;
    if (typeof val === "string") {
        const n = Number(val);
        return n;
    }
    return NaN;
}
/**
 * Coerce a value to a string for comparison.
 */
function toString(val) {
    if (val === null || val === undefined)
        return "";
    return String(val);
}
/**
 * Check if a value is "empty": null, undefined, empty string, or whitespace-only string.
 */
function isEmpty(val) {
    if (val === null || val === undefined)
        return true;
    if (typeof val === "string")
        return val.trim() === "";
    return false;
}
/**
 * Apply a comparison operator to two values.
 * Left is the resolved source, right is the resolved comparison value.
 */
export function applyOperator(operator, left, right) {
    switch (operator) {
        case "equals": {
            if (left === right)
                return true;
            // Coerce: number vs string
            const ln = toNumber(left);
            const rn = toNumber(right);
            if (!isNaN(ln) && !isNaN(rn))
                return ln === rn;
            return toString(left) === toString(right);
        }
        case "not_equals":
            return !applyOperator("equals", left, right);
        case "greater_than": {
            const ln = toNumber(left);
            const rn = toNumber(right);
            if (!isNaN(ln) && !isNaN(rn))
                return ln > rn;
            // Fall back to string comparison (works for ISO dates)
            return toString(left) > toString(right);
        }
        case "less_than": {
            const ln = toNumber(left);
            const rn = toNumber(right);
            if (!isNaN(ln) && !isNaN(rn))
                return ln < rn;
            return toString(left) < toString(right);
        }
        case "greater_or_equal": {
            const ln = toNumber(left);
            const rn = toNumber(right);
            if (!isNaN(ln) && !isNaN(rn))
                return ln >= rn;
            return toString(left) >= toString(right);
        }
        case "less_or_equal": {
            const ln = toNumber(left);
            const rn = toNumber(right);
            if (!isNaN(ln) && !isNaN(rn))
                return ln <= rn;
            return toString(left) <= toString(right);
        }
        case "contains": {
            if (left === null || left === undefined)
                return false;
            // R-263 — array LHS (e.g. batch.formsFound): exact-member match,
            // not substring-of-the-joined-string ("W91" must not match "W9").
            if (Array.isArray(left)) {
                const needle = toString(right).toLowerCase();
                return left.some((v) => toString(v).toLowerCase() === needle);
            }
            return toString(left).toLowerCase().includes(toString(right).toLowerCase());
        }
        case "not_contains":
            return !applyOperator("contains", left, right);
        case "starts_with": {
            if (left === null || left === undefined)
                return false;
            return toString(left).toLowerCase().startsWith(toString(right).toLowerCase());
        }
        case "ends_with": {
            if (left === null || left === undefined)
                return false;
            return toString(left).toLowerCase().endsWith(toString(right).toLowerCase());
        }
        case "in": {
            const list = Array.isArray(right) ? right : [];
            return list.some((item) => applyOperator("equals", left, item));
        }
        case "not_in":
            return !applyOperator("in", left, right);
        case "is_empty":
            return isEmpty(left);
        case "is_not_empty":
            return !isEmpty(left);
        case "matches_regex": {
            if (left === null || left === undefined)
                return false;
            try {
                const regex = new RegExp(toString(right));
                return regex.test(toString(left));
            }
            catch {
                // Invalid regex pattern
                return false;
            }
        }
        case "between": {
            const arr = Array.isArray(right) ? right : [];
            if (arr.length < 2)
                return false;
            const val = toNumber(left);
            const lo = toNumber(arr[0]);
            const hi = toNumber(arr[1]);
            if (isNaN(val) || isNaN(lo) || isNaN(hi))
                return false;
            return val >= lo && val <= hi;
        }
        default:
            return false;
    }
}
//# sourceMappingURL=operators.js.map